import { FilterDropdownButton } from 'components/filters/FilterDropdownButton';
import { FilterChipRow, FilterChipDef } from 'components/filters/FilterChipRow';
import {
  FilterSection,
  FilterSectionTitle,
  FilterOption,
} from 'components/filters/FilterElements';
import { ContentFilters } from 'hooks/useGetSets';
import { AnalyticsService } from 'utils/AnalyticsService';
import { useTranslation } from 'react-i18next';

interface ContentTableFiltersProps {
  filters: ContentFilters;
  onFiltersChange: (filters: ContentFilters) => void;
  getTypeDisplay: (goalType: string) => string;
}

interface ContentTableFilterChipsProps {
  filters: ContentFilters;
  onFiltersChange: (filters: ContentFilters) => void;
  getTypeDisplay: (goalType: string) => string;
  isLoading?: boolean;
}

export const ContentTableFilters = ({ filters, onFiltersChange }: ContentTableFiltersProps) => {
  const { t } = useTranslation();

  const usedInFilters = filters.usedIn || [];
  const typeFilters = filters.goalType || [];
  const sharedStatusFilters = filters.sharedStatus || [];

  const activeFilterCount = usedInFilters.length + typeFilters.length + sharedStatusFilters.length;

  const handleTypeFilterChange = (value: string, checked: boolean) => {
    if (checked) {
      AnalyticsService.getInstance().track('all_content_filter_applied', {
        filter_type: 'content_type',
        filter_value: value,
      });
    }
    const newTypeFilters = checked
      ? [...typeFilters, value]
      : typeFilters.filter((f) => f !== value);
    onFiltersChange({
      ...filters,
      goalType: newTypeFilters as ('set' | 'assessment' | 'survey' | 'scorm')[],
    });
  };

  const handleUsedInFilterChange = (value: string, checked: boolean) => {
    if (checked) {
      AnalyticsService.getInstance().track('all_content_filter_applied', {
        filter_type: 'usage_status',
        filter_value: value,
      });
    }
    onFiltersChange({
      ...filters,
      usedIn: checked ? [value] as ('no_courses' | 'has_courses')[] : [],
    });
  };

  const handleSharedStatusFilterChange = (value: string, checked: boolean) => {
    if (checked) {
      AnalyticsService.getInstance().track('all_content_filter_applied', {
        filter_type: 'shared_status',
        filter_value: value,
      });
    }
    onFiltersChange({
      ...filters,
      sharedStatus: checked ? [value] as ('shared' | 'private')[] : [],
    });
  };

  return (
    <FilterDropdownButton activeFilterCount={activeFilterCount}>
      <FilterSection>
        <FilterSectionTitle>{t('USED IN')}</FilterSectionTitle>
        <FilterOption>
          <input
            type="checkbox"
            checked={usedInFilters.includes('no_courses')}
            onChange={(e) => handleUsedInFilterChange('no_courses', e.target.checked)}
          />
          {t('No courses')}
        </FilterOption>
        <FilterOption>
          <input
            type="checkbox"
            checked={usedInFilters.includes('has_courses')}
            onChange={(e) => handleUsedInFilterChange('has_courses', e.target.checked)}
          />
          {t('1+ courses')}
        </FilterOption>
      </FilterSection>
      <FilterSection>
        <FilterSectionTitle>{t('TYPE')}</FilterSectionTitle>
        <FilterOption>
          <input
            type="checkbox"
            checked={typeFilters.includes('set')}
            onChange={(e) => handleTypeFilterChange('set', e.target.checked)}
          />
          {t('Learning Set')}
        </FilterOption>
        <FilterOption>
          <input
            type="checkbox"
            checked={typeFilters.includes('assessment')}
            onChange={(e) => handleTypeFilterChange('assessment', e.target.checked)}
          />
          {t('Assessment')}
        </FilterOption>
        <FilterOption>
          <input
            type="checkbox"
            checked={typeFilters.includes('survey')}
            onChange={(e) => handleTypeFilterChange('survey', e.target.checked)}
          />
          {t('Survey')}
        </FilterOption>
        <FilterOption>
          <input
            type="checkbox"
            checked={typeFilters.includes('scorm')}
            onChange={(e) => handleTypeFilterChange('scorm', e.target.checked)}
          />
          {t('SCORM')}
        </FilterOption>
      </FilterSection>
      <FilterSection>
        <FilterSectionTitle>{t('SHARED STATUS')}</FilterSectionTitle>
        <FilterOption>
          <input
            type="checkbox"
            checked={sharedStatusFilters.includes('shared')}
            onChange={(e) => handleSharedStatusFilterChange('shared', e.target.checked)}
          />
          {t('Shared')}
        </FilterOption>
        <FilterOption>
          <input
            type="checkbox"
            checked={sharedStatusFilters.includes('private')}
            onChange={(e) => handleSharedStatusFilterChange('private', e.target.checked)}
          />
          {t('Private')}
        </FilterOption>
      </FilterSection>
    </FilterDropdownButton>
  );
};

export const ContentTableFilterChips = ({
  filters,
  onFiltersChange,
  getTypeDisplay,
  isLoading = false,
}: ContentTableFilterChipsProps) => {
  const { t } = useTranslation();

  const usedInFilters = filters.usedIn || [];
  const typeFilters = filters.goalType || [];
  const sharedStatusFilters = filters.sharedStatus || [];

  const removeFilter = (type: 'usedIn' | 'type' | 'sharedStatus', value: string) => {
    if (type === 'usedIn') {
      onFiltersChange({
        ...filters,
        usedIn: usedInFilters.filter((f) => f !== value) as ('no_courses' | 'has_courses')[],
      });
    } else if (type === 'type') {
      onFiltersChange({
        ...filters,
        goalType: typeFilters.filter((f) => f !== value) as ('set' | 'assessment' | 'survey' | 'scorm')[],
      });
    } else {
      onFiltersChange({
        ...filters,
        sharedStatus: sharedStatusFilters.filter((f) => f !== value) as ('shared' | 'private')[],
      });
    }
  };

  const getUsedInLabel = (value: string): string => {
    return value === 'no_courses' ? 'No courses' : '1+ courses';
  };

  const getSharedStatusLabel = (value: string): string => {
    return value === 'shared' ? 'Shared' : 'Private';
  };

  const chips: FilterChipDef[] = [
    ...usedInFilters.map((filter) => ({
      key: `used-in-${filter}`,
      label: `${t('Used In')}: ${getUsedInLabel(filter)}`,
      onDelete: () => removeFilter('usedIn', filter),
    })),
    ...typeFilters.map((filter) => ({
      key: `type-${filter}`,
      label: `${t('Type')}: ${getTypeDisplay(filter)}`,
      onDelete: () => removeFilter('type', filter),
    })),
    ...sharedStatusFilters.map((filter) => ({
      key: `shared-status-${filter}`,
      label: `${t('Shared Status')}: ${getSharedStatusLabel(filter)}`,
      onDelete: () => removeFilter('sharedStatus', filter),
    })),
  ];

  return (
    <FilterChipRow
      chips={chips}
      onClearAll={() => onFiltersChange({})}
      clearAllLabel={t('Clear all filters')}
      isLoading={isLoading}
    />
  );
};
